import React, { useState, useEffect, useCallback } from 'react';
import { appCfgApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useLookups } from '../../context/LookupContext';
import {
  List, Plus, Save, Search, ToggleLeft, ToggleRight, Database, Table2, RefreshCw
} from 'lucide-react';

const LookupValuesPage = () => {
  const { notify } = useNotifications();
  const { refresh: refreshLookupContext } = useLookups();

  // Data
  const [categoryMeta, setCategoryMeta] = useState([]);  // full category objects
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Edit state
  const [editedValues, setEditedValues] = useState({});
  const [newValue, setNewValue] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Category config edit state (for view/query types)
  const [categoryEdits, setCategoryEdits] = useState(null);
  const [dbViews, setDbViews] = useState([]);
  const [viewColumns, setViewColumns] = useState([]);

  // Get selected category metadata
  const selectedCatMeta = categoryMeta.find(c => c.category === selectedCategory);
  const sourceType = selectedCatMeta?.type || 'static';

  // Load categories (full objects)
  const loadCategories = useCallback(async () => {
    try {
      const res = await appCfgApi.getAllLookupCategories();
      setCategoryMeta(res.data);
    } catch (err) {
      notify.error('Failed to load lookup categories');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load values for selected category (static only)
  const loadValues = useCallback(async (category) => {
    if (!category) return;
    try {
      const res = await appCfgApi.getLookupsByCategory(category);
      setValues(res.data);
      setEditedValues({});
    } catch (err) {
      notify.error(`Failed to load values for ${category}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadCategories();
      try {
        const viewsRes = await appCfgApi.getDatabaseViews();
        setDbViews(viewsRes.data);
      } catch (err) { /* views list is optional */ }
      setLoading(false);
    };
    init();
  }, [loadCategories]);

  useEffect(() => {
    if (selectedCategory) {
      const meta = categoryMeta.find(c => c.category === selectedCategory);
      if (meta?.type === 'static') {
        loadValues(selectedCategory);
      } else {
        setValues([]);
        setEditedValues({});
      }
      // Init category config edits
      setCategoryEdits(meta ? { ...meta } : null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, categoryMeta]);

  // Load columns when a view is selected
  useEffect(() => {
    const loadColumns = async () => {
      if (categoryEdits?.type === 'view' && categoryEdits?.view_Name) {
        try {
          const res = await appCfgApi.getViewColumns(categoryEdits.view_Name);
          setViewColumns(res.data);
        } catch (err) {
          setViewColumns([]);
        }
      } else {
        setViewColumns([]);
      }
    };
    loadColumns();
  }, [categoryEdits?.type, categoryEdits?.view_Name]);

  // Auto-select first category
  useEffect(() => {
    if (categoryMeta.length > 0 && !selectedCategory) {
      setSelectedCategory(categoryMeta[0].category);
    }
  }, [categoryMeta, selectedCategory]);

  // Track value edits
  const handleFieldChange = (id, field, value) => {
    setEditedValues(prev => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value }
    }));
  };

  const getFieldValue = (item, field) => {
    if (editedValues[item.id]?.[field] !== undefined) {
      return editedValues[item.id][field];
    }
    return item[field];
  };

  const hasEdits = Object.keys(editedValues).length > 0;

  // Check if category config has been changed
  const hasCategoryEdits = categoryEdits && selectedCatMeta && (
    categoryEdits.type !== selectedCatMeta.type ||
    categoryEdits.validation !== selectedCatMeta.validation ||
    categoryEdits.view_Name !== selectedCatMeta.view_Name ||
    categoryEdits.view_Column !== selectedCatMeta.view_Column
  );

  // Save value edits (static)
  const handleSave = async () => {
    setSaving(true);
    try {
      for (const [id, changes] of Object.entries(editedValues)) {
        const original = values.find(v => v.id === parseInt(id));
        if (!original) continue;
        await appCfgApi.updateLookup(id, {
          ...original,
          ...changes
        });
      }
      notify.success('Lookup values saved');
      setEditedValues({});
      await loadValues(selectedCategory);
      await refreshLookupContext();
    } catch (err) {
      notify.error('Failed to save: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Save category config (source type, view name, column)
  const handleSaveCategoryConfig = async () => {
    if (!categoryEdits) return;
    setSaving(true);
    try {
      await appCfgApi.updateLookupCategory(selectedCategory, categoryEdits);
      notify.success(`Category "${selectedCategory}" config saved`);
      await loadCategories();
      await refreshLookupContext();
      // If switched to static, load values
      if (categoryEdits.type === 'static') {
        await loadValues(selectedCategory);
      }
    } catch (err) {
      notify.error('Failed to save category config: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Add new value
  const handleAddValue = async () => {
    if (!newValue.trim() || !selectedCategory) return;
    try {
      const maxOrder = values.length > 0
        ? Math.max(...values.map(v => v.display_Order)) + 1
        : 1;
      await appCfgApi.createLookup({
        category: selectedCategory,
        value: newValue.trim(),
        display_Order: maxOrder,
        is_Active: true
      });
      setNewValue('');
      notify.success(`Added "${newValue.trim()}" to ${selectedCategory}`);
      await loadValues(selectedCategory);
      await refreshLookupContext();
    } catch (err) {
      notify.error('Failed to add value: ' + (err.response?.data?.message || err.message));
    }
  };

  // Add new category
  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    const catName = newCategory.trim().replace(/\s+/g, '');
    try {
      // Create category entry first
      await appCfgApi.createLookupCategory({
        category: catName,
        type: 'static',
        view_Name: null,
        view_Column: null,
        query: null
      });
      // Create a default value
      await appCfgApi.createLookup({
        category: catName,
        value: '(default)',
        display_Order: 1,
        is_Active: true
      });
      setNewCategory('');
      notify.success(`Category "${catName}" created`);
      await loadCategories();
      setSelectedCategory(catName);
      await refreshLookupContext();
    } catch (err) {
      notify.error('Failed to create category: ' + (err.response?.data?.message || err.message));
    }
  };

  const categories = categoryMeta.map(c => c.category);
  const filteredCategories = categoryMeta.filter(c =>
    !searchTerm || c.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sourceTypeIcon = (type) => {
    if (type === 'view') return <Database size={12} />;
    if (type === 'query') return <Table2 size={12} />;
    return null;
  };

  if (loading) {
    return (
      <div className="dev-module">
        <div className="dev-module-header">
          <div className="dev-module-title"><h1>Lookup Values</h1></div>
        </div>
        <div className="dev-module-content"><p>Loading...</p></div>
      </div>
    );
  }

  return (
    <div className="dev-module lookup-values-page">
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <h1>
            <List size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Lookup Values
          </h1>
          <span className="dev-module-meta">
            {categories.length} categories
          </span>
        </div>
        <div className="dev-module-header-right">
          {sourceType === 'static' && (
            <button className="crud-btn primary" onClick={handleSave} disabled={saving || !hasEdits} title="Save Values">
              <Save size={18} />
            </button>
          )}
          {hasCategoryEdits && (
            <button className="crud-btn primary" onClick={handleSaveCategoryConfig} disabled={saving} title="Save Config">
              <Save size={18} />
            </button>
          )}
          <button
            className="crud-btn"
            onClick={async () => {
              await loadCategories();
              if (selectedCategory) await loadValues(selectedCategory);
              await refreshLookupContext();
              notify.success('Refreshed');
            }}
            title="Refresh"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      <div className="dev-module-content">
        <div className="lookup-layout">
          {/* Category List */}
          <div className="lookup-category-panel">
            <div className="lookup-category-header">
              <div className="lookup-search-box">
                <Search size={14} className="lookup-search-icon" />
                <input
                  type="text"
                  placeholder="Filter categories..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="lookup-search-input"
                />
              </div>
            </div>

            <div className="lookup-category-list">
              {filteredCategories.map(cat => (
                <button
                  key={cat.category}
                  className={`lookup-category-item ${selectedCategory === cat.category ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(cat.category)}
                >
                  <span className="lookup-category-name">{cat.category}</span>
                  {cat.type !== 'static' && (
                    <span className={`lookup-source-badge ${cat.type}`}>
                      {sourceTypeIcon(cat.type)} {cat.type}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="lookup-add-category">
              <input
                type="text"
                placeholder="New category name..."
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                className="lookup-add-input"
              />
              <button
                className="lookup-add-btn"
                onClick={handleAddCategory}
                disabled={!newCategory.trim()}
                title="Add category"
              >
                <Plus size={14} />
              </button>
            </div>
          </div>

          {/* Values Panel */}
          <div className="lookup-values-panel">
            {selectedCategory ? (
              <>
                <div className="lookup-values-header">
                  <h3>{selectedCategory}</h3>
                  <span className="lookup-values-count">
                    {sourceType === 'static' ? `${values.length} values` : `source: ${sourceType}`}
                  </span>
                  <div style={{ flex: 1 }} />
                  {/* Source type selector */}
                  <select
                    className="lookup-source-select"
                    value={categoryEdits?.type || 'static'}
                    onChange={(e) => setCategoryEdits(prev => ({
                      ...prev,
                      type: e.target.value,
                      view_Name: e.target.value === 'static' ? null : prev?.view_Name,
                      view_Column: e.target.value === 'static' ? null : prev?.view_Column
                    }))}
                  >
                    <option value="static">Static</option>
                    <option value="view">SQL View</option>
                    <option value="query">SQL Query</option>
                  </select>
                </div>

                {/* View/Query config */}
                {(categoryEdits?.type === 'view') && (
                  <div className="lookup-source-config">
                    <div className="lookup-config-row">
                      <label>View / Table Name:</label>
                      <select
                        value={categoryEdits?.view_Name || ''}
                        onChange={(e) => setCategoryEdits(prev => ({ ...prev, view_Name: e.target.value || null }))}
                        className="lookup-config-input"
                      >
                        <option value="">-- Select View --</option>
                        {dbViews.map(v => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </select>
                    </div>
                    <div className="lookup-config-row">
                      <label>Column:</label>
                      <select
                        value={categoryEdits?.view_Column || ''}
                        onChange={(e) => setCategoryEdits(prev => ({ ...prev, view_Column: e.target.value || null }))}
                        className="lookup-config-input"
                        disabled={!categoryEdits?.view_Name}
                      >
                        <option value="">{categoryEdits?.view_Name ? '-- Select Column --' : '-- Select a view first --'}</option>
                        {viewColumns.map(col => (
                          <option key={col} value={col}>{col}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {(categoryEdits?.type === 'query') && (
                  <div className="lookup-source-config">
                    <div className="lookup-config-row">
                      <label>SQL Query:</label>
                      <textarea
                        value={categoryEdits?.query || ''}
                        onChange={(e) => setCategoryEdits(prev => ({ ...prev, query: e.target.value || null }))}
                        className="lookup-config-textarea"
                        placeholder="SELECT DISTINCT Column_Name FROM Table_Name ORDER BY Column_Name"
                        rows={3}
                      />
                    </div>
                    <p className="lookup-config-note">⚠ Query support coming soon</p>
                  </div>
                )}

                {/* Static values table */}
                {(categoryEdits?.type === 'static' || sourceType === 'static') && categoryEdits?.type !== 'view' && categoryEdits?.type !== 'query' && (
                  <>
                    <div className="lookup-values-scroll">
                      <table className="lookup-values-table">
                        <thead>
                          <tr>
                            <th style={{ width: 50 }}>Order</th>
                            <th>Value</th>
                            <th style={{ width: 70 }}>Active</th>
                          </tr>
                        </thead>
                        <tbody>
                          {values.map(item => (
                            <tr key={item.id} className={!getFieldValue(item, 'is_Active') ? 'inactive' : ''}>
                              <td>
                                <input
                                  type="number"
                                  value={getFieldValue(item, 'display_Order')}
                                  onChange={(e) => handleFieldChange(item.id, 'display_Order', parseInt(e.target.value) || 0)}
                                  className="lookup-order-input"
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={getFieldValue(item, 'value')}
                                  onChange={(e) => handleFieldChange(item.id, 'value', e.target.value)}
                                  className="lookup-value-input"
                                />
                              </td>
                              <td>
                                <button
                                  className={`lookup-toggle ${getFieldValue(item, 'is_Active') ? 'active' : ''}`}
                                  onClick={() => handleFieldChange(item.id, 'is_Active', !getFieldValue(item, 'is_Active'))}
                                  title={getFieldValue(item, 'is_Active') ? 'Active' : 'Inactive'}
                                >
                                  {getFieldValue(item, 'is_Active')
                                    ? <ToggleRight size={18} />
                                    : <ToggleLeft size={18} />
                                  }
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Add value row */}
                    <div className="lookup-add-value">
                      <input
                        type="text"
                        placeholder="Add new value..."
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddValue()}
                        className="lookup-add-input"
                      />
                      <button
                        className="lookup-add-btn"
                        onClick={handleAddValue}
                        disabled={!newValue.trim()}
                      >
                        <Plus size={14} /> Add
                      </button>
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="lookup-empty">Select a category to view values</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LookupValuesPage;
